import { prettyNumber } from 'src/utils/number-format'
import { STATES_ABBREVIATIONS } from 'src/utils/states'

function formatName(name: string): string {
  // Trim leading and trailing whitespace
  name = name.trim()

  // Define a list of common abbreviations for organizations
  const abbreviations = {
    INC: 'Inc.',
    CORP: 'Corp.',
    LLC: 'LLC',
    LLP: 'LLP',
    LPF: 'LPF',
    CO: 'Co.',
    ASSOC: 'Assoc.',
    LTD: 'Ltd.',
    THE: 'The',
    LP: 'LP',
    NA: 'NA',
  } as const

  // Function to capitalize a single name part (for person names)
  function capitalizePart(part: string) {
    if (part.length === 1) {
      // If it's a single character (an initial), keep it uppercase
      return part.toUpperCase()
    }
    // Capitalize the first letter and lowercase the rest
    return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
  }

  // Function to capitalize significant words (for organization names)
  function capitalizeWord(word: string) {
    const upperWord = word.toUpperCase() as keyof typeof abbreviations
    // Check if the word is an abbreviation and use its standard format
    if (abbreviations[upperWord]) {
      return abbreviations[upperWord]
    }
    // Capitalize the first letter of each word
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  }

  // Check if the name contains any organization abbreviations
  const isOrganization = Object.keys(abbreviations).some((abbr) => name.toUpperCase().includes(abbr))

  if (isOrganization) {
    // Format as an organization name
    const words = name.split(/\s+/)
    const formattedWords = words.map((word) => capitalizeWord(word))
    let formattedName = formattedWords.join(' ')
    // Remove any unnecessary punctuation (e.g., multiple commas)
    formattedName = formattedName.replace(/,+/g, ',')
    formattedName = formattedName.replace(/\s+([.,])/g, '$1') // Remove space before punctuation
    return formattedName
  } else {
    // Format as a person name
    const parts = name.split(/\s+/)
    const formattedParts = parts.map((part) => capitalizePart(part))
    return formattedParts.join(' ')
  }
}

function formatAddress(address: string): string {
  // Regex to match state abbreviation or full state name
  const stateRegex = new RegExp(
    `\\b(${Object.keys(STATES_ABBREVIATIONS).join('|')})\\b|\\b(${Object.values(STATES_ABBREVIATIONS).join('|')})\\b`,
    'gi'
  )

  // Find the state
  const stateMatch = address.match(stateRegex)
  let state = ''
  let stringAfterState = address
  let stringBeforeState = ''

  if (stateMatch) {
    // use the last match in case of multiple matches (closest to the end of the string)
    state = stateMatch[stateMatch.length - 1]
    // Before converting the state get the remaining string proceeding/preceeding where the state was found
    const stateIndex = address.indexOf(state)
    stringAfterState = address.slice(stateIndex + state.length).trim()
    stringBeforeState = address.slice(0, stateIndex).trim()

    // State operations
    const findState = Object.values(STATES_ABBREVIATIONS).includes(state)
    if (findState) {
      // Get the abbreviation of the state
      state = Object.keys(STATES_ABBREVIATIONS).find((key) => STATES_ABBREVIATIONS[key] === state) || ''
    }
  }

  // If we didn't find a state, treat the entire string as the street address
  if (!stateMatch) {
    return address
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
  }

  // Extract the ZIP code
  const zipCodeMatch = stringAfterState.match(/\b\d{5}(?:-\d{4})?\b/)
  let zipCode = ''

  if (zipCodeMatch) {
    zipCode = zipCodeMatch[0]
    stringAfterState = stringAfterState.replace(zipCode, '').trim()
  }

  // Titlecase the remaining address
  const streetCity = stringBeforeState
    .split(', ')
    .map((part) =>
      part
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ')
    )
    .join(', ')

  // Combine into the final format
  return `${streetCity} ${state.toUpperCase()} ${zipCode}`.trim() || '--'
}

export function formatString(str: string | undefined | null, type: 'name' | 'address'): string {
  if (!str) {
    return '--'
  }
  switch (type) {
    case 'name':
      return formatName(str)
    case 'address':
      return formatAddress(str)
    default:
      return str
  }
}

// Format distance (miles)
export function formatDistance(miles: number): string {
  const MILES_TO_FEET = 5280

  if (miles < 0.25) {
    const feet = miles * MILES_TO_FEET
    return `${feet.toFixed(0)} ft`
  }

  return `${miles.toFixed(2)} mi`
}

export const formatDifferencePercentage = (difference: number, fixed = 1) => {
  const symbol = difference > 0 ? '+' : ''
  const percentage = prettyNumber((difference * 100).toFixed(fixed))
  return `${symbol}${percentage}%`
}
